<template>
    <BaseModal
        title="Import Students"
        :loading="loading"
        submit-text="Import"
        max-width="2xl"
        :show-footer="false"
        @close="$emit('close')"
        @submit="handleImport"
    >
        <!-- Header Content -->
        <div class="sm:flex sm:items-start">
            <div
                class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 sm:mx-0 sm:h-10 sm:w-10"
            >
                <Upload class="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <h3
                    class="text-base font-semibold leading-6 text-gray-900 dark:text-white"
                    id="modal-title"
                >
                    Import Students
                </h3>
                <div class="mt-2">
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                        Upload an Excel file (.xlsx) to import students. The
                        file should contain the following columns:
                    </p>
                    <ul
                        class="mt-2 text-xs text-gray-500 dark:text-gray-400 list-disc list-inside"
                    >
                        <li>NAMA (Name)</li>
                        <li>NISN</li>
                        <li>TANGGAL LAHIR (YYYY-MM-DD)</li>
                        <li>JENIS KELAMIN (MALE/FEMALE)</li>
                        <li>EMAIL</li>
                        <li>TELEPHONE</li>
                        <li>ALAMAT (Address)</li>
                        <li>EMERGENCY CONTACT NAME</li>
                        <li>EMERGENCY CONTACT TELEPHONE</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Body Content -->
        <div class="mt-4">
            <label
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
                Select Excel File
            </label>
            <input
                ref="fileInput"
                type="file"
                accept=".xlsx,.xls,.csv"
                @change="handleFileChange"
                class="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900 dark:file:text-blue-200 hover:file:bg-blue-100 dark:hover:file:bg-blue-800 cursor-pointer"
            />
            <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Maximum file size: 2MB. Supported formats: .xlsx, .xls, .csv
            </p>
        </div>

        <!-- Download Template -->
        <div class="mt-4 rounded-md bg-green-50 dark:bg-green-900/20 p-4">
            <h4
                class="mb-2 text-sm font-medium text-green-800 dark:text-green-200"
            >
                Need a template?
            </h4>
            <p class="mb-3 text-xs text-green-700 dark:text-green-300">
                Download Excel template to see required format
            </p>
            <button
                type="button"
                @click="downloadTemplate"
                class="inline-flex items-center rounded-md border border-transparent bg-green-100 px-3 py-2 text-xs font-medium text-green-700 hover:bg-green-200 dark:bg-green-800 dark:text-green-100 dark:hover:bg-green-700"
            >
                <Download class="mr-2 h-4 w-4" />
                Download Template
            </button>
        </div>

        <!-- Error Message -->
        <div
            v-if="error || errorTitle"
            class="mt-4 rounded-md bg-red-50 dark:bg-red-900/20 p-4"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <AlertCircle class="h-5 w-5 text-red-400" />
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-red-800 dark:text-red-200"
                    >
                        {{ errorTitle }}
                    </h3>
                    <div
                        v-if="validationErrors.length > 0"
                        class="mt-2 space-y-2"
                    >
                        <div
                            v-for="(ve, idx) in validationErrors"
                            :key="idx"
                            class="text-sm text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 rounded p-2"
                        >
                            <span class="font-medium"
                                >Row {{ ve.lokasi_row }}</span
                            >
                            <span class="text-red-500">
                                - {{ ve.lokasi_column }}</span
                            >
                            <ul class="list-disc list-inside mt-1">
                                <li v-for="err in ve.errors" :key="err">
                                    {{ err }}
                                </li>
                            </ul>
                        </div>
                    </div>
                    <p
                        v-else
                        class="mt-1 text-sm text-red-700 dark:text-red-300"
                    >
                        {{ error }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Footer -->
        <div
            class="mt-6 -mx-6 -mb-6 bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6"
        >
            <button
                type="button"
                @click="handleImport"
                :disabled="!selectedFile || loading"
                class="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                <span v-if="loading" class="flex items-center">
                    <Loader2
                        class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    />
                    Importing...
                </span>
                <span v-else>Import</span>
            </button>
            <button
                type="button"
                @click="$emit('close')"
                class="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-600 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:w-auto transition-colors"
            >
                Cancel
            </button>
        </div>
    </BaseModal>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { Upload, Download, AlertCircle, Loader2 } from "@lucide/vue";
import { studentApi } from "@/api/student";
import BaseModal from "@/components/BaseModal.vue";

const emit = defineEmits<{
    (e: "close"): void;
    (e: "success"): void;
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const selectedFile = ref<File | null>(null);
const loading = ref(false);
const error = ref("");
const errorTitle = ref("");
const validationErrors = ref<any[]>([]);

const handleFileChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (file) {
        const validExtensions = [".xlsx", ".xls", ".csv"];

        const fileExtension = file.name
            .substring(file.name.lastIndexOf("."))
            .toLowerCase();

        if (!validExtensions.includes(fileExtension)) {
            error.value =
                "Please select a valid Excel file (.xlsx, .xls) or CSV file.";
            selectedFile.value = null;
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            error.value = "File size must be less than 2MB.";
            selectedFile.value = null;
            return;
        }

        selectedFile.value = file;
        error.value = "";
        errorTitle.value = "";
        validationErrors.value = [];
    }
};

const handleImport = async () => {
    if (!selectedFile.value) {
        error.value = "Please select a file to import.";
        return;
    }

    loading.value = true;
    error.value = "";
    errorTitle.value = "";
    validationErrors.value = [];

    try {
        const formData = new FormData();
        formData.append("file", selectedFile.value);

        const response = await studentApi.importStudents(formData);

        if (response.success) {
            emit("success");
            emit("close");
        } else {
            error.value =
                response.message || "Import failed. Please try again.";
        }
    } catch (err: any) {
        console.error("Import error:", err);

        if (err.response) {
            const responseData = err.response.data;
            const errorData = responseData.data;

            if (errorData && Array.isArray(errorData) && errorData.length > 0) {
                errorTitle.value = responseData.message || "Validation errors:";
                validationErrors.value = errorData;
                error.value = "";
            } else {
                error.value =
                    responseData.message ||
                    "Import failed. Please check your file and try again.";
            }
        } else if (err.request) {
            error.value =
                "Network error. Please check your connection and try again.";
        } else {
            error.value = "An unexpected error occurred. Please try again.";
        }
    } finally {
        loading.value = false;
    }
};

const downloadTemplate = () => {
    const templateUrl = "/template-excel/TEMPLATE_IMPORT_STUDENT.xlsx";
    const link = document.createElement("a");
    link.href = templateUrl;
    link.download = "TEMPLATE_IMPORT_STUDENT.xlsx";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
</script>
